// Components==============
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import indexActivities from 'actions/activity/index';
import indexPhases from 'actions/phase/index';
import indexProjectGroup from 'actions/project-group/index';
import indexProject from 'actions/project/index';
import indexReflection from 'actions/reflection/index';
import indexTheme from 'actions/theme/index';
import indexTraining from 'actions/training/training/index';
import Layout from 'components/Layout/Layout';
import Button from 'components/UI/Button/Button';
import Summary from 'components/UI/Summary/Summary';
import { usePermission } from 'hooks/usePermission';
import { ProcessGroups, useProcessGroups } from 'hooks/useProcessGroups';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import updateProject from 'actions/project/update';
import Body from 'pages/Workspace/ProjectDetails/components/Summary/Body/Body';
import TopLeft from 'pages/Workspace/ProjectDetails/components/Summary/TopLeft/TopLeft';
import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { sortOnName } from 'utilities/sortOnName';
import Top from './components/Top/Top';
import { StudentListEntity, useStudentList } from './components/useStudentList';
import './Reflect.module.scss';
import { ReflectFunctions, useReflectFunctions } from './useReflectFunctions';
import EndReflectionView from './Views/EndReflectionView';
import GroupView from './Views/GroupView';
import Overview from './Views/Overview';
import PeerReflectionView from './Views/PeerReflectionView';
// =========================

export type ReflectContextType = {
  loading?: boolean;
  isTeacher: boolean;
  currentStudent?: StudentListEntity;
  sortedStudentList: StudentListEntity[];
  selectedStudent: string;
  setSelectedStudent: React.Dispatch<React.SetStateAction<string>>;
  updateCounter: number;
  setUpdateCounter: React.Dispatch<React.SetStateAction<number>>;
  processGroups?: ProcessGroups;
  checkpointGroups: ProcessProcessEntity[][];
  project?: ProjectProjectEntity;
  theme?: ThemeEntity;
  dispatchingDreams: boolean;
  setDispatchingDreams: React.Dispatch<React.SetStateAction<boolean>>;
  checkpointGroup?: ProcessProcessEntity[];
  processes?: (ProcessProcessEntity | undefined)[];
  endPointActivities: (ProcessProcessEntity | undefined)[];
  endPointActivity?: ProcessProcessEntity | undefined;
  peerId?: string;
  wrapperRef: React.RefObject<HTMLDivElement>;
  allReflectionsAreCompleted: boolean;
  scrollToCard: (ref: React.RefObject<HTMLDivElement>) => void;
  toggled: boolean;
  onClickToggle: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  handleLockProject: () => void;
  handleUnlockProject: () => void;
};

export const ReflectContext = createContext(
  {} as ReflectContextType & ReflectFunctions
);

export default function Reflect() {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const history = useHistory();

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(indexTheme());
    dispatch(indexProject());
    dispatch(indexTraining());
    dispatch(indexProjectGroup());
    dispatch(indexActivities());
    dispatch(indexPhases());
    dispatch(indexReflection());
  }, []);

  const { projectId, checkpointId, peerId } = useParams() as {
    projectId: string;
    checkpointId?: string;
    peerId?: string;
  };

  // Toggle to open/close all refelction cards
  const [toggled, setIsToggled] = useState(false);
  const onClickToggle = () => setIsToggled((prev) => !prev);

  const [updateCounter, setUpdateCounter] = useState(0);
  const [dispatchingDreams, setDispatchingDreams] = useState(false);

  const project = useRedux((state) => state.project?.entities.byId[projectId]);
  const processes = useRedux((state) =>
    project?.process?.map((id) => state.process?.entities.byId[id])
  )?.sort((a, b) => parseInt(a?.order || '0') - parseInt(b?.order || '0'));

  const theme = useRedux((state) =>
    peerId
      ? state.reflection?.entities.byId?.[parseInt(peerId)]?.project_process
          ?.theme
      : project?.theme_id
      ? state.theme?.entities.byId?.[project?.theme_id]
      : undefined
  );

  const images = theme?.attachments
    ?.filter((a) => a.attachment_type === 'image')
    .map((a) => JSON.parse(a.attachment_data).file_path);

  const [isOpen, setIsOpen] = useState(false);

  //   student selector
  const studentList = useStudentList(theme);

  const sortedStudentList = useMemo(() => sortOnName(studentList), [
    studentList.length,
  ]);

  const [selectedStudent, setSelectedStudent] = useState(
    (sortedStudentList?.find((student) => student?.projectId === project?.id)
      ?.name as string) || ''
  );

  const currentStudent = studentList?.find(
    (student) => student.name === selectedStudent
  );

  const [counter, setCounter] = useState(0);

  useEffect(() => {
    setCounter(1);
    if (counter > 0) {
      history.push(`/groei/reflecteren/${currentStudent?.projectId}`);
    }
  }, [selectedStudent]);
  //

  const isTeacher = usePermission(['Docent', 'super-admin', 'Extern']);

  const loading = useRedux(
    (state) =>
      state.theme?.loading ||
      state.project?.loading ||
      state.projectGroup?.loading ||
      state.activity?.loading ||
      state.phase?.loading ||
      state.reflection?.loading
  );

  const { processGroups } = useProcessGroups(project);

  const endPointActivities =
    processes?.filter(
      (p) => p?.process_type !== 'App\\Phase' && p?.process_id !== 3
    ) || [];

  const endPointActivity = processes?.find(
    (p) => p?.process_type === 'App\\Phase' && p?.process_id === 7
  );

  const checkpointGroups: ProcessProcessEntity[][] = [[]];

  processes?.forEach((p) => {
    if (p) {
      if (p.process_type === 'App\\Phase' && p.process_id !== 7) return;

      const lastArray = checkpointGroups[checkpointGroups.length - 1];

      if (
        lastArray.find(
          (t) => t?.process_type === 'App\\Activity' && t.process_id === 3
        )
      )
        return checkpointGroups.push([p]);

      lastArray.push(p);
    }
  });

  const checkpointGroup = checkpointId
    ? checkpointGroups.find(
        (group) => group && `${group?.[group.length - 1]?.id}` === checkpointId
      )
    : undefined;

  const isEndReflection =
    isTeacher ||
    (checkpointGroup &&
      checkpointGroup?.[checkpointGroup.length - 1].process_type ===
        'App\\Phase');

  const allReflectionsAreCompleted = useRedux((state) => {
    const reflections = state.reflection?.entities.allIds
      .map((id) => state.reflection?.entities.byId[id])
      .filter((r) => {
        const student = isTeacher
          ? currentStudent?.userId
          : state?.authentication?.user.id;

        return (
          r?.created_by === student &&
          r?.project_process?.project_id === project?.id
        );
      });

    const endPoint = reflections?.find(
      (r) => r?.project_process?.process_type === 'App\\Phase'
    );

    const endReflectionCompleted =
      // check if array exists to prevent endpoint status from going to completed while the questions aren't set yet
      endPoint?.note?.length &&
      endPoint?.project_process?.process_type === 'App\\Phase'
        ? endPoint?.note?.find((qna) => !qna.answer) === undefined
        : false;

    return endReflectionCompleted;
  });

  const scrollToCard = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ inline: 'start' });
  };

  function handleLockProject() {
    dispatch(
      updateProject({
        id: project?.id,
        setAssessmentLock: true,
        locked_for_assessment: 1,
      })
    );
    console.log('Project locked');
  }

  // TODO: add unlock project mutation, return error now
  function handleUnlockProject() {
    dispatch(
      updateProject({
        id: project?.id,
        setAssessmentLock: true,
        locked_for_assessment: 0,
      })
    );
  }

  const contextValues = {
    currentStudent,
    isTeacher,
    loading,
    sortedStudentList,
    selectedStudent,
    setSelectedStudent,
    processGroups,
    checkpointGroups,
    processes,
    project,
    theme,
    dispatchingDreams,
    setDispatchingDreams,
    checkpointGroup,
    endPointActivities,
    endPointActivity,
    updateCounter,
    setUpdateCounter,
    peerId,
    wrapperRef,
    allReflectionsAreCompleted,
    scrollToCard,
    toggled,
    setIsToggled,
    onClickToggle,
    handleLockProject,
    handleUnlockProject,
  };

  const functions = useReflectFunctions(contextValues);

  return (
    <Layout grey>
      <ReflectContext.Provider value={{ ...functions, ...contextValues }}>
        <Summary
          type="reflection"
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          topLeftContent={
            <TopLeft theme={theme} isOpen={isOpen} loading={loading} />
          }
          topRightContent={
            !peerId ? (
              <Button
                onClick={() =>
                  history.push(`/werkplaats/project-details/${projectId}`)
                }
                color="grey"
              >
                {translate('general.terms.to-project')}
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            ) : undefined
          }
          bodyContent={<Body theme={theme} />}
          images={images}
          video={theme?.youtube_link}
          isPeerReflection={!!peerId}
        />
        <Top />
        {peerId ? (
          <PeerReflectionView />
        ) : isEndReflection ? (
          <EndReflectionView />
        ) : checkpointId ? (
          <GroupView />
        ) : (
          <Overview />
        )}
      </ReflectContext.Provider>
    </Layout>
  );
}
